import { useEffect, useState, useCallback } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Box, Button, Grid, Typography, Alert, 
  FormControlLabel, Checkbox, Accordion, AccordionSummary, 
  AccordionDetails
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DropZone from '../common/DropZone';
import { generateSlug } from '../../utils/slugGenerator';
import { uploadSingleImage } from '../../services/upload.service';

// Slug removed from schema — auto-generated
const templesSchema = z.object({
  slug: z.string().optional(), // Hidden, auto-generated
  name: z.string().min(1, 'Укажите название'),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Некорректный email').optional().or(z.literal('')),
  // В базе сайты храмов записаны голыми доменами (hram-luki.ru, елисаветинский-храм.рф),
  // и z.string().url() не давал сохранить карточку. Формат проверяет бэкенд.
  website: z.string().optional(),
  schedule: z.string().optional(),
  history: z.string().optional(),
  description: z.string().optional(),
  directions: z.string().optional(),
  photoUrl: z.string().optional(), // Uploaded via DropZone
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  isActive: z.boolean().default(true),
});

type TemplesFormValues = z.infer<typeof templesSchema>;

// Координаты редактируются как текст, а API принимает числа — перевод делает
// handleFormSubmit, чтобы вход и выход схемы формы совпадали.
type TempleSubmitValues = Omit<TemplesFormValues, 'latitude' | 'longitude'> & {
  latitude?: number;
  longitude?: number;
};

interface TemplesFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TempleSubmitValues) => Promise<void>;
  initialData?: TemplesFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function TemplesForm({ open, onClose, onSubmit, initialData, loading, error }: TemplesFormProps) {
  const [photoUploading, setPhotoUploading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TemplesFormValues>({
    resolver: zodResolver(templesSchema),
    defaultValues: {
      slug: '',
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      schedule: '',
      history: '',
      description: '',
      directions: '',
      photoUrl: '',
      latitude: '',
      longitude: '',
      isActive: true,
    },
  });

  const nameValue = watch('name');
  const photoUrl = watch('photoUrl');

  // Auto-generate slug when name changes (only for new items)
  useEffect(() => {
    if (!initialData && nameValue && nameValue.trim().length > 0) {
      const autoSlug = generateSlug(nameValue);
      setValue('slug', autoSlug);
    }
  }, [nameValue, initialData, setValue]);

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    } else {
      reset({
        slug: '',
        name: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        schedule: '',
        history: '',
        description: '',
        directions: '',
        photoUrl: '',
        latitude: '',
        longitude: '',
        isActive: true,
      });
    }
  }, [initialData, open, reset]);

  // Handle image upload via DropZone
  const handleImageUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setPhotoUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadSingleImage(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setPhotoUploading(false);
    }
  }, []);

  const handleFormSubmit = useCallback(async (data: TemplesFormValues) => {
    await onSubmit({
      ...data,
      latitude: data.latitude ? parseFloat(data.latitude) : undefined,
      longitude: data.longitude ? parseFloat(data.longitude) : undefined,
    });
  }, [onSubmit]);

  const handlePhotoChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('photoUrl', url);
  }, [setValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Редактировать храм' : 'Добавить храм'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* === MAIN INFO === */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Основная информация
              </Typography>
            </Grid>
            
            {/* Name — main field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название *"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
            
            {/* Slug — hidden field, shown as read-only hint */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="URL (автоматически)"
                value={watch('slug') || ''}
                disabled
                helperText="Генерируется из названия"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    bgcolor: 'action.hover',
                  }
                }}
              />
            </Grid>
            
            {/* isActive checkbox */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox {...register('isActive')} defaultChecked />}
                label="Храм активен"
              />
            </Grid>
            
            {/* Address */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Адрес"
                {...register('address')}
              />
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Описание"
                multiline
                rows={3}
                {...register('description')}
              />
            </Grid>
            
            {/* Photo — DropZone instead of URL TextField */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Фотография храма
              </Typography>
              <DropZone
                onUpload={handleImageUpload}
                type="image"
                maxFiles={1}
                maxSize={10}
                multiple={false}
                value={photoUrl || ''}
                onChange={handlePhotoChange}
                label="Перетащите изображение или нажмите для выбора"
                helperText="JPG, PNG, WebP до 10MB"
              />
              {photoUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка...
                </Typography>
              )}
            </Grid>
            
            {/* === CONTACT INFO (Collapsible) === */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Контактная информация</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Телефон"
                        {...register('phone')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        {...register('email')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Веб-сайт"
                        error={!!errors.website}
                        helperText={errors.website?.message}
                        {...register('website')}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            {/* === SCHEDULE (Collapsible) === */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Расписание богослужений</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    label="Расписание"
                    multiline
                    rows={4}
                    {...register('schedule')}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            {/* === LOCATION (Collapsible) === */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Расположение</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Широта"
                        {...register('latitude')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Долгота"
                        {...register('longitude')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Как добраться"
                        multiline
                        rows={3}
                        {...register('directions')}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            {/* === HISTORY (Collapsible) === */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>История храма</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    label="История"
                    multiline
                    rows={6}
                    {...register('history')}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSubmit(handleFormSubmit)} variant="contained" disabled={loading || photoUploading}>
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}